import { useState } from "react";
import { Search, Filter, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { FAQSearchFilters } from "@/types/faq";
import { Badge } from "@/components/ui/badge";

interface FAQSearchBarProps {
  onSearch: (filters: FAQSearchFilters) => void;
  placeholder?: string;
}

export const FAQSearchBar = ({
  onSearch,
  placeholder = "Caută întrebări despre NIS2...",
}: FAQSearchBarProps) => {
  const [keyword, setKeyword] = useState("");
  const [entityType, setEntityType] =
    useState<FAQSearchFilters["entityType"]>("all");
  const [urgency, setUrgency] = useState<FAQSearchFilters["urgency"]>("all");
  const [showFilters, setShowFilters] = useState(false);

  const handleSearch = () => {
    onSearch({
      keyword: keyword || undefined,
      entityType,
      urgency,
    });
  };

  const clearFilters = () => {
    setKeyword("");
    setEntityType("all");
    setUrgency("all");
    onSearch({});
  };

  const hasActiveFilters = keyword || entityType !== "all" || urgency !== "all";

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder={placeholder}
            className="pl-10 pr-10 h-12"
          />
          {keyword && (
            <button
              onClick={() => {
                setKeyword("");
                handleSearch();
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Popover open={showFilters} onOpenChange={setShowFilters}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="lg" className="gap-2">
              <Filter className="w-4 h-4" />
              Filtre
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">
                  {
                    [keyword, entityType !== "all", urgency !== "all"].filter(
                      Boolean
                    ).length
                  }
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="end">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-sm">Filtre avansate</h4>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="h-auto p-0 text-xs"
                  >
                    Resetează
                  </Button>
                )}
              </div>

              <div className="space-y-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Tip entitate</label>
                  <Select
                    value={entityType}
                    onValueChange={(value) =>
                      setEntityType(value as FAQSearchFilters["entityType"])
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toate</SelectItem>
                      <SelectItem value="esential">
                        Entități esențiale
                      </SelectItem>
                      <SelectItem value="important">
                        Entități importante
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Urgență</label>
                  <Select
                    value={urgency}
                    onValueChange={(value) =>
                      setUrgency(value as FAQSearchFilters["urgency"])
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toate</SelectItem>
                      <SelectItem value="high">Urgent</SelectItem>
                      <SelectItem value="medium">Mediu</SelectItem>
                      <SelectItem value="low">Scăzut</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button onClick={handleSearch} className="w-full">
                Aplică filtre
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        <Button onClick={handleSearch} size="lg">
          Caută
        </Button>
      </div>

      {hasActiveFilters && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">Filtre active:</span>
          {keyword && (
            <Badge variant="secondary" className="gap-1">
              {keyword}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => {
                  setKeyword("");
                  handleSearch();
                }}
              />
            </Badge>
          )}
          {entityType !== "all" && (
            <Badge variant="secondary" className="gap-1">
              {entityType === "esential"
                ? "Entități esențiale"
                : "Entități importante"}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => {
                  setEntityType("all");
                  handleSearch();
                }}
              />
            </Badge>
          )}
          {urgency !== "all" && (
            <Badge variant="secondary" className="gap-1">
              {urgency === "high"
                ? "Urgent"
                : urgency === "medium"
                  ? "Mediu"
                  : "Scăzut"}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => {
                  setUrgency("all");
                  handleSearch();
                }}
              />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};
